import { Puzzle, Share2 } from 'lucide-react';
import type { SVGProps } from 'react';

import { cn } from '@/lib/cn';

type IconProps = SVGProps<SVGSVGElement>;

const Svg = ({ className, children, ...props }: IconProps & { children: React.ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={cn('h-5 w-5', className)}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
);

export const NotificationsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </Svg>
);

export const DaemonTriggersIcon = (props: IconProps) => (
  <Svg {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </Svg>
);

export const CoActionsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
    />
  </Svg>
);

export const PlansIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
    />
  </Svg>
);

export const CompletionReportsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </Svg>
);

export const CodeReviewsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12l2 2 4-4M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm2 4h6"
    />
  </Svg>
);

export const PostMortemsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 17v-2m3 2v-4m3 4V7m2 12H7a2 2 0 01-2-2V5a2 2 0 012-2h7.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V17a2 2 0 01-2 2z"
    />
  </Svg>
);

export const ConventionsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.567 2 8.5v10C2 16.567 4.462 15 7.5 15c1.746 0 3.332.483 4.5 1.253m0-10C13.168 5.483 14.754 5 16.5 5c3.038 0 5.5 1.567 5.5 3.5v10c0-1.933-2.462-3.5-5.5-3.5-1.746 0-3.332.483-4.5 1.253"
    />
  </Svg>
);

// 러너(DaemonTriggersIcon)가 번개를 쓰므로 스킬은 다른 모양이어야 한다 — 같은 path를 쓰면
// 사이드바에서 두 메뉴가 구분되지 않는다. 퍼즐 조각은 "필요할 때 끼워 넣는 능력"이라는
// 스킬의 판단 축과도 맞는다.
export const SkillsIcon = ({ className, ...props }: IconProps) => (
  <Puzzle className={cn('h-5 w-5', className)} aria-hidden="true" {...props} />
);

// 공유 탐색은 같은 스킬 아이콘을 쓰면 목록 메뉴와 구분되지 않는다. 공유 의미의
// Share2로 스킬 목록과 나란히 둔다.
export const SharedSkillsIcon = ({ className, ...props }: IconProps) => (
  <Share2 className={cn('h-5 w-5', className)} aria-hidden="true" {...props} />
);

export const DocumentsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M7 4h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 4v5h5M8 13h8M8 17h6" />
  </Svg>
);

export const RunnerModelsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 01-1.806-1.741 12 12 0 01.054-3.693A2 2 0 016.26 8.126l.5.047a6 6 0 003.466-.672l.088-.044a6 6 0 014.07-.57l1.065.177a2 2 0 011.59 1.248l.3.8a6 6 0 001.678 2.55v0"
    />
  </Svg>
);

export const SettingsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </Svg>
);

export const ConfigIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </Svg>
);

export const MembersIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17 20h5V4H2v16h5m10 0v-2a4 4 0 00-4-4H11a4 4 0 00-4 4v2m10 0H7m10-8a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </Svg>
);

export const TeamsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17 20h5v-2a4 4 0 00-3-3.874M17 20H7m10 0v-2a4 4 0 00-4-4h-2a4 4 0 00-4 4v2m0 0H2v-2a4 4 0 013-3.874M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 2a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM8 9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
    />
  </Svg>
);

export const FeedbacksIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M7 8h10M7 12h6m5 8l-4-4H6a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2v4z"
    />
  </Svg>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <Svg className={cn('h-4 w-4', props.className)} {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
  </Svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <Svg className={cn('h-4 w-4', props.className)} {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </Svg>
);

export const MonitoringIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </Svg>
);

export const SearchIndexIcon = (props: IconProps) => (
  <Svg {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 6c0 1.657 3.582 3 8 3s8-1.343 8-3-3.582-3-8-3-8 1.343-8 3zm0 0v6c0 1.657 3.582 3 8 3m8-9v4m-16 2v6c0 1.657 3.582 3 8 3m5-7a4 4 0 100 8 4 4 0 000-8zm3 7l3 3"
    />
  </Svg>
);
